/** Superadmin → Safety: enable/disable the daily video gate, schedule mode
 * (same video / per weekday / per job) with an upload slot per selection,
 * plus the morning nudge and daily emailed record. */
import { useEffect, useState } from "react";
import { Power, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SafetySlot from "./SafetySlot";
import SafetySchedules from "./SafetySchedules";
import SafetyQuizSettings from "./SafetyQuizSettings";
import SafetyLibrary from "./SafetyLibrary";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const MODES = [["single", "Same video every day"], ["weekday", "Different video per weekday"], ["job", "Different video per job"]];

export default function SafetySettingsTab() {
  const [s, setS] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pick, setPick] = useState("");
  const [extraJobs, setExtraJobs] = useState([]);   // job slots added but not uploaded yet
  const [library, setLibrary] = useState([]);
  const loadLibrary = () => api.get("/safety/library").then(({ data }) => setLibrary(data || [])).catch(() => {});
  useEffect(() => {
    api.get("/safety/settings").then(({ data }) => setS(data)).catch(() => setS({ enabled: false, slots: {}, schedule_mode: "single" }));
    api.get("/safety/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
    loadLibrary();
  }, []);

  const save = async (patch, msg) => {
    try { const { data } = await api.put("/safety/settings", patch); setS(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  if (!s) return <div className="py-6 text-sm text-gray-500">Loading…</div>;

  const slots = s.slots || {};
  const hasDefault = !!slots.default?.has_video;
  const jobKeys = [...new Set([...Object.keys(slots).filter((k) => k.startsWith("job:")), ...extraJobs])];
  const projectName = (id) => projects.find((p) => p.project_id === id)?.name || slots[`job:${id}`]?.project_name || id;
  const onSlotChange = (data) => { setS(data); loadLibrary(); };

  return (
    <div className="space-y-5" data-testid="settings-safety-tab">
      <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Daily safety meeting video</div>
          <p className="mt-1 text-xs text-gray-500">When ON, every employee must watch the day's video and hit CONFIRM the first time they clock in each day. Confirmations are logged under Operations → Safety meeting with time, IP and location.</p>
        </div>
        <button onClick={() => save({ enabled: !s.enabled }, s.enabled ? "Safety meeting is OFF" : "Safety meeting is ON — required at first clock-in each day")}
          disabled={!hasDefault && !s.enabled} title={!hasDefault ? "Upload the default video first" : ""} aria-pressed={!!s.enabled}
          className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-bold ${s.enabled ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-600"} disabled:opacity-50`} data-testid="safety-enabled-toggle">
          <Power className="h-3.5 w-3.5" /> {s.enabled ? "ON" : "OFF"}
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 space-y-3" data-testid="safety-schedule-section">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-auto text-sm font-semibold text-gray-900 dark:text-white">Which video plays</div>
          <select value={s.schedule_mode || "single"} onChange={(e) => save({ schedule_mode: e.target.value }, `Schedule: ${MODES.find((m) => m[0] === e.target.value)?.[1]}`)}
            className="rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs text-gray-900 dark:text-white dark:[&>option]:text-black" data-testid="safety-schedule-mode">
            {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <SafetySlot slotKey="default" label={s.schedule_mode === "single" ? "Safety video" : "Default video (fallback)"} meta={slots.default} onChange={onSlotChange} canRemove={!s.enabled} hint="Upload the video every employee watches" library={library} />

        {s.schedule_mode === "weekday" && (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="safety-weekday-slots">
            {DAYS.map(([k, l]) => <SafetySlot key={k} slotKey={k} label={l} meta={slots[k]} onChange={onSlotChange} library={library} />)}
          </div>
        )}

        {s.schedule_mode === "job" && (
          <div className="space-y-2" data-testid="safety-job-slots">
            <p className="text-xs text-gray-500">Employees see the job's video after picking the job at clock-in; jobs without their own video play the default.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} className="min-w-[14rem] rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs text-gray-900 dark:text-white dark:[&>option]:text-black" data-testid="safety-job-pick">
                <option value="">Select a project…</option>
                {projects.filter((p) => !jobKeys.includes(`job:${p.project_id}`)).map((p) => <option key={p.project_id} value={p.project_id}>{p.name}{p.po ? ` · PO ${p.po}` : ""}</option>)}
              </select>
              <button onClick={() => { if (pick) { setExtraJobs((x) => [...x, `job:${pick}`]); setPick(""); } }} disabled={!pick} className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="safety-job-add"><Plus className="h-3.5 w-3.5" /> Add job slot</button>
            </div>
            {jobKeys.map((k) => <SafetySlot key={k} slotKey={k} label={projectName(k.slice(4))} meta={slots[k]} onChange={(d) => { onSlotChange(d); setExtraJobs((x) => x.filter((e) => e !== k)); }} hint="Upload this job's video" library={library} />)}
          </div>
        )}
      </div>

      <SafetyQuizSettings s={s} onChange={setS} />
      <SafetyLibrary items={library} slots={slots} onReload={loadLibrary} onSettings={setS} />
      <SafetySchedules s={s} onChange={setS} />
    </div>
  );
}
